/**
 * chain-eos/mechat/payments.ts — token transfers between two accounts.
 *
 * A payment is an ordinary `transfer` action, not a mechat message: nothing is
 * encrypted and nothing extra is written on chain. Showing payments inside the
 * conversation is purely a reading trick — we pull the account's transfer history
 * and keep the ones whose counterparty is the peer. That means transfers sent
 * from PayCash, an exchange, or any other wallet also appear in the thread.
 */

import { HYPERION_ENDPOINTS } from '../config.js'

export interface ChainPayment {
  trxId: string
  timestamp: string
  from: string
  to: string
  /** Raw asset string, e.g. `1.0000 EOS`. */
  quantity: string
  amount: number
  symbol: string
  /** Token contract (`eosio.token`, `core.vaulta`, …). */
  contract: string
  memo: string
  direction: 'in' | 'out'
}

interface HyperionTransfer {
  trx_id: string
  timestamp: string
  act: {
    account: string
    name: string
    data: { from?: string; to?: string; quantity?: string; memo?: string }
  }
}

export interface FetchPaymentsOptions {
  account: string
  /** Only keep transfers to/from this counterparty. Omit to keep all. */
  peer?: string
  limit?: number
  signal?: AbortSignal
}

function parseQuantity(quantity: string): { amount: number; symbol: string } {
  const [value, symbol = ''] = quantity.trim().split(' ')
  const amount = Number.parseFloat(value)
  return { amount: Number.isFinite(amount) ? amount : 0, symbol }
}

/**
 * Transfers involving `account`, newest first. Tries each Hyperion endpoint;
 * returns [] rather than throwing — payments are decoration on the conversation,
 * they must never break the messages themselves.
 */
export async function fetchPayments(options: FetchPaymentsOptions): Promise<ChainPayment[]> {
  const account = String(options.account ?? '').trim()
  if (!account) return []

  const params = new URLSearchParams({
    account,
    filter: '*:transfer',
    limit: String(Math.min(options.limit ?? 100, 500)),
    sort: 'desc',
  })

  for (const endpoint of HYPERION_ENDPOINTS) {
    try {
      const res = await fetch(`${endpoint}/v2/history/get_actions?${params.toString()}`, {
        signal: options.signal,
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const body = (await res.json()) as { actions?: HyperionTransfer[] }

      const seen = new Set<string>()
      const out: ChainPayment[] = []
      for (const a of body.actions ?? []) {
        const d = a.act?.data
        if (a.act?.name !== 'transfer' || !d?.quantity || !d.from || !d.to) continue
        if (d.from !== account && d.to !== account) continue
        if (options.peer && d.from !== options.peer && d.to !== options.peer) continue
        // One transfer produces several receipts — keep the first copy only.
        const dedupeKey = `${a.trx_id}:${d.from}:${d.to}:${d.quantity}`
        if (seen.has(dedupeKey)) continue
        seen.add(dedupeKey)

        const { amount, symbol } = parseQuantity(d.quantity)
        out.push({
          trxId: a.trx_id,
          timestamp: a.timestamp,
          from: d.from,
          to: d.to,
          quantity: d.quantity,
          amount,
          symbol,
          contract: a.act.account,
          memo: d.memo ?? '',
          direction: d.from === account ? 'out' : 'in',
        })
      }
      return out
    } catch (err) {
      console.warn(`[mechat] Hyperion transfer history failed on ${endpoint}:`, err)
    }
  }
  return []
}
